// components/WaterRankingsCard.jsx — Water Rankings compact board (web, v2.1 restored).
// =============================================================================
// REPLACES THE CORRUPTED FILE (confirmed Jul 24 2026): the previous content
// of this file was a stale save-over of WTRBottleScreen.jsx that exported
// WTRBottleScreen and IMPORTED ITSELF, so the live app rendered a second
// full copy of the bottle screen at Section 8.5. This file restores the
// documented v2.1 contract (Jul 14 2026), ported from the native
// spec-derived implementation (src/components/WaterRankingsCard.js in
// generosity-wtr-app-native), so web and native render the same board.
//
//   Compact board read directly below Water Quality History. Server-shaped
//   by tier (middle-path gate): plus sees the top of the full board, free
//   sees the top source plus a locked count. Tapping any row or SEE ALL
//   opens the full Water Rankings screen (CompareMyWatersScreen overlay).
//
// Data: GET {apiBase}/api/bottle/compare/sources?email= — the SAME endpoint
// the full overlay reads, so the compact board and the full board can never
// tell different stories.
//
// Honesty rules (identical to the overlay, do not remove):
//   - The client never sorts, never scores, never breaks ties. Rows render
//     in server order.
//   - Evidence badge on every row; a published number never renders
//     unbadged beside a measured one.
//   - No winner is pre-declared; the top line quotes the server's rank.
//   - Free tier renders the locked COUNT with no data behind it.
//
// Drop-in: WTRBottleScreen already passes the final props
// (apiBase, onOpenRankings). No emoji (no-emoji ruling, Jul 24 2026):
// the free-tier lock is a stroke SVG glyph.
// =============================================================================

import { useEffect, useState } from "react";
import { authHeaders } from "../lib/auth";
import { readProfile } from "../lib/profile";

const C = {
  white: "#FFFFFF", blue: "#51B0E6", blueSoft: "#EAF5FC", gray: "#A6A8AB",
  light: "#F0F1F3", ink: "#232A33", green: "#2E7D5B", amber: "#C7A230",
  rust: "#C96B2B", red: "#B4433A", line: "rgba(15, 20, 25, 0.06)",
};

const BAND_COLOR = { premium: C.green, good: C.blue, moderate: C.amber, poor: C.rust, very_poor: C.red };
const bandColor = (band) => BAND_COLOR[band] || C.gray;

function getEmail() {
  const email = ((readProfile().email) || "").trim().toLowerCase();
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) ? email : null;
}

// LockGlyph: brand SVG replacing the lock emoji (no-emoji ruling, Jul 24
// 2026, matching the native card).
function LockGlyph({ size = 13, color = C.blue }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" style={{ display: "block", flexShrink: 0 }}>
      <rect x="4.5" y="10.5" width="15" height="10" rx="2.5" fill="none" stroke={color} strokeWidth="2" />
      <path d="M8 10.5 V7.5 a4 4 0 0 1 8 0 V10.5" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" />
      <path d="M12 14.2 v2.6" stroke={color} strokeWidth="2" strokeLinecap="round" fill="none" />
    </svg>
  );
}

function EvidenceBadge({ evidence }) {
  const map = {
    measured: { label: "MEASURED", color: C.blue, bg: C.blueSoft },
    label_data: { label: "LABEL DATA", color: C.gray, bg: C.light },
    municipal_record: { label: "MUNICIPAL RECORD", color: C.gray, bg: C.light },
    independent_test: { label: "INDEPENDENT TEST", color: C.gray, bg: C.light },
  };
  const m = map[evidence] || map.label_data;
  return (
    <span style={{
      background: m.bg, borderRadius: 999, padding: "2px 7px",
      fontSize: 8.5, fontWeight: 700, letterSpacing: 0.7, color: m.color,
      fontFamily: "'Barlow Condensed', Montserrat, sans-serif", whiteSpace: "nowrap",
    }}>
      {m.label}
    </span>
  );
}

export default function WaterRankingsCard({ apiBase = "", onOpenRankings = () => {} }) {
  const [board, setBoard] = useState(null);
  const [tier, setTier] = useState("plus");
  const [lockedCount, setLockedCount] = useState(0);
  const [state, setState] = useState("loading"); // loading | ready | error

  useEffect(() => {
    let cancelled = false;
    async function load() {
      const email = getEmail();
      if (!email) { setState("error"); return; }
      try {
        const params = new URLSearchParams({ email });
        const res = await fetch(`${apiBase}/api/bottle/compare/sources?${params.toString()}`, {
          headers: authHeaders(),
        });
        if (!res.ok) throw new Error(String(res.status));
        const json = await res.json();
        if (cancelled) return;
        setBoard(json.board || []);
        setTier(json.tier === "free" ? "free" : "plus");
        setLockedCount(Number(json.lockedCount) || 0);
        setState("ready");
      } catch (e) {
        if (!cancelled) setState("error");
      }
    }
    load();
    return () => { cancelled = true; };
  }, [apiBase]);

  const label = (
    <div style={{ fontSize: 10, fontWeight: 700, color: C.gray, letterSpacing: 1.5, textTransform: "uppercase" }}>
      Water Rankings
    </div>
  );

  // Quiet card in every non-ready state: the compact board is a shortcut,
  // never a source of alarm. Errors degrade to the plain open link.
  if (state === "loading") {
    return (
      <div className="card-default" style={{ padding: "18px 16px", marginBottom: 14 }}>
        {label}
        <div style={{ height: 40, background: C.light, borderRadius: 10, marginTop: 8 }} />
        <div style={{ height: 40, background: C.light, borderRadius: 10, marginTop: 6 }} />
      </div>
    );
  }

  if (state === "error" || !board || board.length === 0) {
    return (
      <div className="card-default" style={{ padding: "18px 16px", marginBottom: 14 }}>
        {label}
        <div style={{ fontSize: 12, lineHeight: 1.5, color: "#3D4043", marginTop: 8 }}>
          {state === "error"
            ? "Rankings will load next time you open this screen."
            : "Run a water test and tag its source to start your personal scoreboard."}
        </div>
        <button
          onClick={onOpenRankings}
          style={{
            marginTop: 10, background: "transparent", border: "none", padding: 0,
            fontSize: 10.5, fontWeight: 800, letterSpacing: 0.4, color: C.blue,
            cursor: "pointer", fontFamily: "inherit",
          }}
        >
          OPEN WATER RANKINGS {"\u2192"}
        </button>
      </div>
    );
  }

  // Server order preserved verbatim; the compact board shows the top rows only.
  const rows = board.slice(0, tier === "free" ? 1 : 3);

  return (
    <div className="card-default" style={{ padding: "18px 16px", marginBottom: 14 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
        {label}
        <button
          onClick={onOpenRankings}
          style={{
            background: "transparent", border: "none", padding: "4px 0",
            fontSize: 10.5, fontWeight: 800, letterSpacing: 0.4, color: C.blue,
            cursor: "pointer", fontFamily: "inherit",
          }}
        >
          SEE ALL {"\u2192"}
        </button>
      </div>

      {rows.map((s, i) => (
        <div
          key={s.id}
          onClick={onOpenRankings}
          style={{
            display: "flex", alignItems: "center", gap: 12, padding: "10px 0",
            borderBottom: i < rows.length - 1 ? `1px solid ${C.line}` : "none",
            cursor: "pointer",
          }}
        >
          <span style={{
            width: 20, textAlign: "center", fontSize: 13, fontWeight: 800,
            color: s.ranked ? C.ink : C.gray, fontVariantNumeric: "tabular-nums", flexShrink: 0,
          }}>
            {s.ranked ? s.rank : "\u00B7"}
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 7, flexWrap: "wrap" }}>
              <span style={{
                fontSize: 13, fontWeight: 700, color: C.ink, maxWidth: "55%",
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
              }}>
                {s.name}
              </span>
              <EvidenceBadge evidence={s.evidence} />
            </div>
            <div style={{
              fontSize: 10.5, color: C.gray, marginTop: 3,
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}>
              {s.tds != null ? `${Math.round(s.tds)} \u00B7 ${s.tdsWord}` : "No scored tests yet"}
              {s.testCount > 0 ? ` \u00B7 ${s.testCount} test${s.testCount === 1 ? "" : "s"}` : ""}
              {!s.ranked ? " \u00B7 keep testing to rank" : ""}
            </div>
          </div>
          <span style={{
            fontSize: 20, fontWeight: 800, width: 34, textAlign: "right",
            color: bandColor(s.scoreBand), fontVariantNumeric: "tabular-nums", flexShrink: 0,
            fontFamily: "'Barlow Condensed', Montserrat, sans-serif",
          }}>
            {s.score == null ? "\u00B7\u00B7" : Math.round(s.score)}
          </span>
        </div>
      ))}

      {tier === "free" && lockedCount > 0 && (
        <div
          onClick={onOpenRankings}
          style={{
            display: "flex", alignItems: "center", gap: 8,
            background: C.blueSoft, borderRadius: 12, padding: "10px 12px", marginTop: 10,
            cursor: "pointer",
          }}
        >
          <LockGlyph />
          <span style={{ flex: 1, fontSize: 11.5, lineHeight: 1.5, color: C.ink, fontWeight: 500 }}>
            {lockedCount === 1
              ? "1 more of your waters is ranked in WTR+."
              : `${lockedCount} more of your waters are ranked in WTR+.`}
          </span>
        </div>
      )}

      <div style={{ fontSize: 10, lineHeight: 1.5, color: C.gray, marginTop: 10 }}>
        Ranked by your measured tests only. Sources rank after 3 scored tests.
      </div>
    </div>
  );
}
